/**
 * Division Dashboard field aliases (Requirements 7.1-7.3).
 *
 * The backend returns the same metric under more than one name: `current_year_sales`
 * in some payloads, `cy_sales` or `CurrentYearSales` in others. That variance used to
 * be handled by scattered `?? r.cy_sales` fallbacks inside every mapper in
 * `PharmaDashboard/services/api.js`. It lives here instead, as one table plus two
 * pure functions, so the accepted names can be reasoned about (and property-tested)
 * in one place.
 *
 * Normalization never drops data: a row keyed by an alias comes back keyed by the
 * canonical name, and every key that is not in the table passes through untouched
 * (Req 7.3).
 *
 * Nothing here performs I/O or reads the environment.
 */

/**
 * Canonical field name -> accepted aliases, in precedence order.
 *
 * The canonical key wins whenever the row carries it; among aliases, the earliest one
 * listed wins, which keeps normalization deterministic for rows that carry several.
 *
 * @type {Readonly<Record<string, string[]>>}
 */
export const FIELD_ALIASES = {
  current_year_sales: ['cy_sales', 'CurrentYearSales', 'current_sales'],
  last_year_sales: ['ly_sales', 'LastYearSales'],
  target_value: ['target', 'budget', 'total_budget'],
  achievement_pct: ['ach_pct', 'achievement_percentage'],
  growth_pct: ['grth_pct', 'growth_percentage'],
  current_month_sales: ['CurrentMonthSales'],
  avg_6month_sales: ['Avg6MonthSales'],
  change_pct: ['ChangePercentage', 'change_percentage'],
  total_tms: ['tms_total'],
  tms_achieved: ['tms_ach'],
  sop_count: ['sop'],
  // Credit-note components and the BDM label: the short spellings on the right are the
  // ones the `bdm-summary` branch of `services/api.js` used to fall back to inline.
  rate_difference: ['rate_diff'],
  sales_return_value: ['returns'],
  expiry_value: ['expiry'],
  breakage_value: ['breakage'],
  bdm_name: ['bdm'],
};

/** True when the row carries a usable value under `key`. `undefined` counts as absent. */
function hasValue(row, key) {
  return Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined;
}

/** Builds alias -> canonical lookup, ignoring aliases that collide with a canonical name. */
function buildAliasIndex(aliases) {
  const index = new Map();
  Object.entries(aliases || {}).forEach(([canonical, aliasList]) => {
    (Array.isArray(aliasList) ? aliasList : []).forEach((alias) => {
      if (typeof alias !== 'string' || alias === '' || alias === canonical) return;
      if (Object.prototype.hasOwnProperty.call(aliases, alias)) return;
      if (!index.has(alias)) index.set(alias, canonical);
    });
  });
  return index;
}

/**
 * Rewrites one response row so every mapped metric is readable under its canonical name.
 *
 * Aliased keys are consumed rather than copied alongside the canonical name, so a row
 * written with canonical keys and the same row written with aliases normalize to
 * deep-equal objects (Req 7.1). Keys the table says nothing about are copied through
 * exactly as they arrived, which is what makes this safe to call at the top of a
 * mapper that also reads unmapped fields such as `expiry_value` or `hq_name`
 * (Req 7.3).
 *
 * Precedence when a row carries duplicates: the canonical key first, then aliases in
 * the order they appear in the table. Malformed rows (`null`, `undefined`, primitives,
 * arrays) yield `{}` so callers can keep reading properties without a guard.
 *
 * @param {Record<string, unknown>|null|undefined} row
 * @param {Record<string, string[]>} [aliases=FIELD_ALIASES]
 * @returns {Record<string, unknown>} a new row; the input is never mutated
 */
export function normalizeAliasedRow(row, aliases = FIELD_ALIASES) {
  if (row === null || typeof row !== 'object' || Array.isArray(row)) return {};

  const aliasIndex = buildAliasIndex(aliases);
  const normalized = {};

  // Pass 1: copy everything that is not an alias, preserving insertion order.
  Object.keys(row).forEach((key) => {
    if (aliasIndex.has(key)) return;
    normalized[key] = row[key];
  });

  // Pass 2: fill canonical names that the row only supplied under an alias.
  Object.entries(aliases || {}).forEach(([canonical, aliasList]) => {
    if (hasValue(normalized, canonical)) return;
    const winner = (Array.isArray(aliasList) ? aliasList : []).find(
      (alias) => aliasIndex.get(alias) === canonical && hasValue(row, alias),
    );
    if (winner !== undefined) normalized[canonical] = row[winner];
  });

  return normalized;
}

/**
 * `normalizeAliasedRow` over a collection, preserving length and order.
 *
 * A non-array payload (`null`, an error envelope, a single object) yields `[]` so
 * mappers can call `.map` on the result unconditionally.
 *
 * @param {unknown[]|null|undefined} rows
 * @param {Record<string, string[]>} [aliases=FIELD_ALIASES]
 * @returns {Record<string, unknown>[]}
 */
export function normalizeAliasedRows(rows, aliases = FIELD_ALIASES) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => normalizeAliasedRow(row, aliases));
}

/** Fields a division object may carry, in the order they are trusted. */
const DIVISION_NAME_KEYS = ['division', 'name', 'label', 'value', 'division_name'];

/** Extracts a division's display name from a string, a number, or an object. */
function resolveDivisionName(entry) {
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'number' && Number.isFinite(entry)) return String(entry);
  if (entry === null || typeof entry !== 'object') return '';

  for (const key of DIVISION_NAME_KEYS) {
    const candidate = entry[key];
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate);
  }
  return '';
}

/**
 * Normalizes a `/divisions` payload into dropdown options.
 *
 * The endpoint returns either plain strings (`["ACUTE"]`) or objects
 * (`[{ division: "ACUTE" }]`), and older responses wrap the list in `{ rows }` or
 * `{ divisions }`. All of those collapse to `[{ value, label }]` here so `FilterBar`
 * can map options without knowing the wire shape (Req 7.2). Length and order are
 * preserved for every entry that names a division; entries that name nothing (`null`,
 * `""`, an object with no usable field) are dropped, because a blank option is not
 * selectable. Duplicates are kept — deduping is the caller's decision.
 *
 * @param {unknown} payload the `/divisions` response body
 * @returns {{ value: string, label: string }[]}
 */
export function normalizeDivisionOptions(payload) {
  let list = payload;
  if (!Array.isArray(list) && list !== null && typeof list === 'object') {
    if (Array.isArray(list.rows)) list = list.rows;
    else if (Array.isArray(list.divisions)) list = list.divisions;
    else if (Array.isArray(list.data)) list = list.data;
  }
  if (!Array.isArray(list)) return [];

  return list.reduce((options, entry) => {
    const name = resolveDivisionName(entry);
    if (name !== '') options.push({ value: name, label: name });
    return options;
  }, []);
}
